import { Box } from '@mui/joy';
import { FilterModel, SortModelItem } from 'ag-grid-community';
import dayjs from 'dayjs';
import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridServerTable, SlidingPanelContent } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { formatDateForServer } from 'core/utils';
import { partService } from 'parts/services';
import { IPartHistoryRequestPayload, IReusePriceConfig } from 'parts/types';

import { columnDef } from './columns';
import { PartDetailsTab } from '../enums';
import { usePartDetailsScrollToFocus } from '../hooks';

const MIN_ROWS_FOR_FOCUS_TRIGGER = 3;

const PAGE_SIZE = 100;

interface IProps {
  initialState: {
    filter: {
      filterModel: FilterModel;
    };
    sort: {
      sortModel: SortModelItem[];
    };
  };
  requestPayload: IPartHistoryRequestPayload;
  reusePrice?: IReusePriceConfig;
  fullWidth?: boolean;
}

export const History = ({ initialState, requestPayload, reusePrice, fullWidth = true }: IProps) => {
  const { t } = useTranslation();

  const { panelScrollAnchorRef, onBodyScroll } = usePartDetailsScrollToFocus(
    PartDetailsTab.HISTORY,
    { minRowsForTrigger: MIN_ROWS_FOR_FOCUS_TRIGGER }
  );

  const columnDefs = useMemo(() => columnDef({ reusePrice }), [reusePrice]);

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'quantity':
          return `"${value}"`;
        default:
          return value;
      }
    });

    return mappedFilters;
  }, []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      const today = dayjs().startOf('day');
      const todayMinus12Months = today.subtract(12, 'month').startOf('day');

      const modifiedRequestPayload = {
        ...requestPayload,
        filters: {
          ...requestPayload.filters,
          updatedAtDateRange: `${formatDateForServer(todayMinus12Months)}|${formatDateForServer(
            today
          )}`,
          ...mapFilters(filters),
        },
        ...rest,
      };

      return partService.getPartHistory(modifiedRequestPayload);
    },
    [mapFilters, requestPayload]
  );

  return (
    <Box ref={panelScrollAnchorRef} sx={{ display: 'contents' }}>
      <SelectionCountProvider>
        <AgGridServerTableProvider
          tableId="partHistoryTable"
          entityType="QuotePart"
          columnDefs={columnDefs}
          initialState={initialState}
          getRows={getRows}
          pageSize={PAGE_SIZE}
          hasRowSelection={false}
          layoutType={TableLayoutType.PART_QUOTE_HISTORY}
          hasLayoutActions
          hasManageFilters
          onBodyScroll={onBodyScroll}
        >
          <SlidingPanelContent title={t('titleHistory')} fullWidth={fullWidth}>
            <AgGridServerTable />
          </SlidingPanelContent>
        </AgGridServerTableProvider>
      </SelectionCountProvider>
    </Box>
  );
};
